import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { AdminService } from 'src/app/services/admin.service';
import { SessionCleanupService } from 'src/app/services/session-cleanup.service';

interface MenuNode {
  label?: string;
  route?: string;
  children?: MenuNode[];
}

@Injectable({
  providedIn: 'root'
})
export class MenuAccessService {
  private authorizedRoutes = new Set<string>();
  private authorizedPrefixes: string[] = [];
  private routeLabels = new Map<string, string>();
  private snapshotLoaded = false;
  private bootValidated = false;
  private bootValidationPromise: Promise<void> | null = null;
  private storageSyncStarted = false;

  private readonly alwaysAllowedRoutes = new Set<string>([
    '',
    'dashboards/analytics',
    'login',
    'register',
    'forgot-password',
    'reset-password',
    'coming-soon',
    'access-denied',
    'feedback/public',
    'feedback/public/thank-you'
  ]);

  private readonly alwaysAllowedPrefixes = [
    'finance/payment/print',
    'finance/receipt/print',
    'finance/voucher/print',
    'finance/contra/print',
    'finance/creditnote/print',
    'finance/debitnote/print',
    'purchase/purchaseorder/print',
    'purchase/purchasebill/print',
    'sales/invoice/invoice-print',
    'store/materialrequest/print',
    'store/materialissue/print',
    'store/materialtransfer/print',
    'store/grnmaster/print'
  ];

  constructor(
    private readonly router: Router,
    private readonly adminService: AdminService,
    private readonly sessionCleanup: SessionCleanupService
  ) {
    this.refreshFromStorage();
  }

  startCrossTabSync(): void {
    if (this.storageSyncStarted) {
      return;
    }

    this.storageSyncStarted = true;

    window.addEventListener('storage', (event: StorageEvent) => {
      if (!event.key || !['menu', 'token', 'tokenaccess'].includes(event.key)) {
        return;
      }

      this.bootValidated = false;
      this.bootValidationPromise = null;
      this.refreshFromStorage();

      if (event.key === 'token' && !event.newValue) {
        this.sessionCleanup.logoutToLogin();
        return;
      }

      this.redirectIfCurrentRouteIsUnauthorized();
    });
  }

  async ensureMenuSnapshot(): Promise<void> {
    if (this.bootValidated) {
      return;
    }

    if (this.bootValidationPromise) {
      return this.bootValidationPromise;
    }

    this.bootValidationPromise = this.loadMenuSnapshotForCurrentSession()
      .finally(() => {
        this.bootValidated = true;
        this.bootValidationPromise = null;
      });

    return this.bootValidationPromise;
  }

  refreshFromStorage(): void {
    const rawMenu = localStorage.getItem('menu');
    if (!rawMenu) {
      this.authorizedRoutes.clear();
      this.authorizedPrefixes = [];
      this.routeLabels.clear();
      this.snapshotLoaded = false;
      return;
    }

    try {
      const menu = JSON.parse(rawMenu) as MenuNode[];
      this.refreshFromMenu(menu);
    } catch {
      this.authorizedRoutes.clear();
      this.authorizedPrefixes = [];
      this.routeLabels.clear();
      this.snapshotLoaded = false;
    }
  }

  refreshFromMenu(menu: MenuNode[]): void {
    const routes = new Set<string>();
    const routeLabels = new Map<string, string>();
    this.collectRoutes(menu, routes, routeLabels);
    this.authorizedRoutes = routes;
    this.routeLabels = routeLabels;
    this.authorizedPrefixes = Array.from(routes)
      .filter((route) => route.length > 0)
      .sort((a, b) => b.length - a.length);
    this.snapshotLoaded = true;
  }

  hasAccess(url: string): boolean {
    if (this.isUnrestrictedRole()) {
      return true;
    }

    const normalizedUrl = this.normalizeUrl(url);

    if (this.alwaysAllowedRoutes.has(normalizedUrl)) {
      return true;
    }

    if (this.alwaysAllowedPrefixes.some((prefix) => this.matchesPrefix(normalizedUrl, prefix))) {
      return true;
    }

    if (!this.snapshotLoaded) {
      // Avoid blocking navigation before the first menu snapshot is available.
      return true;
    }

    if (this.authorizedRoutes.has(normalizedUrl)) {
      return true;
    }

    return this.authorizedPrefixes.some((prefix) => this.matchesPrefix(normalizedUrl, prefix));
  }

  normalizeUrl(url: string): string {
    const [pathWithoutQuery] = String(url || '').split('?');
    const [pathWithoutFragment] = pathWithoutQuery.split('#');
    const path = pathWithoutFragment
      .replace(/^\/+/, '')
      .replace(/\/+$/, '');

    return path;
  }

  getMenuLabel(url: string): string | null {
    const normalizedUrl = this.normalizeUrl(url);

    if (this.routeLabels.has(normalizedUrl)) {
      return this.routeLabels.get(normalizedUrl) || null;
    }

    for (const prefix of this.authorizedPrefixes) {
      if (this.matchesPrefix(normalizedUrl, prefix) && this.routeLabels.has(prefix)) {
        return this.routeLabels.get(prefix) || null;
      }
    }

    return null;
  }

  private collectRoutes(nodes: MenuNode[], routes: Set<string>, routeLabels: Map<string, string>): void {
    for (const node of nodes || []) {
      const normalizedRoute = this.normalizeUrl(node?.route || '');
      if (normalizedRoute) {
        routes.add(normalizedRoute);
        if (node?.label) {
          routeLabels.set(normalizedRoute, node.label);
        }
      }

      if (Array.isArray(node?.children) && node.children.length > 0) {
        this.collectRoutes(node.children, routes, routeLabels);
      }
    }
  }

  private matchesPrefix(url: string, prefix: string): boolean {
    return url === prefix || url.startsWith(`${prefix}/`);
  }

  private isUnrestrictedRole(): boolean {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    if (!token) {
      return false;
    }

    try {
      const decodedToken: any = jwtDecode(token);
      return Number(decodedToken?.role) === 0;
    } catch {
      return false;
    }
  }

  private async loadMenuSnapshotForCurrentSession(): Promise<void> {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    if (!token) {
      this.refreshFromStorage();
      return;
    }

    let decodedToken: any = null;

    try {
      decodedToken = jwtDecode(token);
    } catch {
      this.refreshFromStorage();
      return;
    }

    const rawRoleId = decodedToken?.role;
    const roleId = Number(rawRoleId);

    if (rawRoleId === null || rawRoleId === undefined || Number.isNaN(roleId)) {
      this.refreshFromStorage();
      return;
    }

    try {
      const menu = await firstValueFrom(this.adminService.getFullmenulist(roleId, token)) as MenuNode[];
      localStorage.setItem('menu', JSON.stringify(menu));
      this.refreshFromMenu(Array.isArray(menu) ? menu : []);
    } catch (error: any) {
      if (error?.status === 401) {
        this.sessionCleanup.logoutToLogin();
        return;
      }

      this.refreshFromStorage();
    }
  }

  private redirectIfCurrentRouteIsUnauthorized(): void {
    const currentUrl = this.normalizeUrl(this.router.url);

    if (!currentUrl || currentUrl === 'access-denied') {
      return;
    }

    if (this.hasAccess(currentUrl)) {
      return;
    }

    this.router.navigate(['/access-denied'], {
      queryParams: {
        from: currentUrl || 'home'
      }
    });
  }
}
